import { ReactNode } from 'react';

import { ContentHeader, GridFields } from 'components';
import { scrollToTop } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  Box,
  Button,
  CircularProgress,
  Input,
  Stack,
  Typography,
} from 'libs/ui';

import { VersionList } from '../../components';
import { VersionType } from '../../types';

import {
  ChangesContentStyled,
  ContentStyled,
  LoadingContentStyled,
} from './styles';

export interface VersionViewProps<V extends VersionType> {
  content: ReactNode;
  topContent?: ReactNode;
  versionId: string;
  name: string;
  versions: V[];
  loading?: boolean;
  error?: string;
  onCloseVersionHistory?: () => void;
  onChangeVersion: (version: V) => void;
}

export const VersionView = <V extends VersionType>({
  content,
  topContent,
  versions,
  name,
  versionId,
  loading,
  error,
  onChangeVersion,
  onCloseVersionHistory,
}: VersionViewProps<V>) => {
  const { t } = useTranslation();

  const headerRightContent = (
    <Button
      colorVariant="gray"
      label={t('client.client.versions.closeVersionHistoryLabel')}
      mode="outlined"
      sizeVariant="s"
      onClick={onCloseVersionHistory}
    />
  );

  const currentVersion = versions.find(({ id }) => id === versionId);

  const handlerOnChangeVersion = (version: V) => {
    scrollToTop();
    onChangeVersion(version);
  };

  const headerTitle = t('client.client.versions.header', { name });

  return (
    <ContentStyled>
      <Box className="clients-sidebar">
        <VersionList
          versionId={versionId}
          versions={versions}
          onClickVersion={handlerOnChangeVersion}
        />
      </Box>
      <Box className="clients-content">
        <ContentHeader
          header={headerTitle}
          headerAfterContent={topContent}
          rightContent={headerRightContent}
          title={headerTitle}
        />

        {loading ? (
          <LoadingContentStyled alignItems="center" justifyContent="center">
            <CircularProgress color="success" size={40} />
          </LoadingContentStyled>
        ) : error ? (
          <Typography>{error}</Typography>
        ) : (
          <Stack spacing={36}>
            <ChangesContentStyled spacing={12}>
              <Typography variant="subtitle">
                {t('client.client.versions.changes.title')}
              </Typography>
              <GridFields>
                <Input
                  readOnly
                  showCopyButton
                  label={t('client.client.versions.changes.changed.label')}
                  value={t('client.client.versions.changes.changed.value', {
                    name: currentVersion?.updatedEntity.fullName,
                  })}
                />
                <Input
                  readOnly
                  showCopyButton
                  label={t('client.client.versions.changes.reason.label')}
                  value={currentVersion?.reason}
                />
              </GridFields>
            </ChangesContentStyled>
            {content}
          </Stack>
        )}
      </Box>
    </ContentStyled>
  );
};
